import { ApiErrorClassificationInterface, ApiErrorKindType } from './classifyApiError.types'

const fetchNetworkErrorMessages = new Set([
  'failed to fetch',
  'fetch failed',
  'load failed',
  'networkerror when attempting to fetch resource',
  'networkerror when attempting to fetch resource.',
  'network request failed',
])

/**
 * Determines whether the value carries an HTTP Response object.
 * RestClient attaches `error.response = Response` for all non-ok HTTP replies.
 */
const hasResponseObject = (value: unknown): value is { response: Response } =>
  value !== null && typeof value === 'object' && 'response' in value && (value as Record<string, unknown>).response instanceof Response

const isFetchNetworkError = (error: unknown): error is TypeError =>
  error instanceof TypeError && fetchNetworkErrorMessages.has(error.message.trim().toLowerCase())

export const classifyApiError = (error: unknown): ApiErrorClassificationInterface => {
  // --- Abort (fetch cancelled on unmount / filter change) ---
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { kind: 'abort' }
  }

  // Safari/WebKit fires TypeError('Cancelled') instead of DOMException('AbortError')
  if (error instanceof TypeError && error.message.trim().toLowerCase() === 'cancelled') {
    return { kind: 'abort' }
  }

  // --- Network (TypeError from fetch: no connection, DNS, CORS, etc.) ---
  if (isFetchNetworkError(error)) {
    return { kind: 'network' }
  }

  // --- HTTP errors thrown by RestClient (plain object + .response: Response) ---
  if (hasResponseObject(error)) {
    const { status } = error.response

    if (status === 401) {
      return { kind: 'unauthorized', status }
    }

    if (status === 403) {
      return { kind: 'forbidden', status }
    }

    if (status === 429) {
      return { kind: 'rate_limit', status }
    }

    if (status >= 400 && status < 500) {
      return { kind: 'client', status }
    }

    if (status >= 500) {
      return { kind: 'server', status }
    }

    return { kind: 'unknown', status }
  }

  // --- Parse / contract error ---
  if (error instanceof SyntaxError) {
    return { kind: 'parse' }
  }

  return { kind: 'unknown' }
}

/**
 * Returns true for error kinds that should be dropped silently — no Sentry,
 * no user notification. All error handlers use this as their early-exit guard.
 *
 *  - abort        — request cancelled on unmount / filter change
 *  - unauthorized — HTTP 401, redirect handled by AuthProvider
 *  - rate_limit   — HTTP 429, expected throttling
 */
const silentApiErrorKinds = new Set<ApiErrorKindType>(['abort', 'rate_limit', 'unauthorized'])

export const isSilentApiError = (kind: ApiErrorKindType): boolean => silentApiErrorKinds.has(kind)
